import { FC } from 'react';
import { EntryResultEnum } from 'src/ts/enums';
import { EntryResultType, IProof } from 'src/ts/interfaces';
import { getAttrEntryResult } from 'src/helpers/getEntryStatusIcon';
import {
  FormValues,
  getIconPath,
  getProofRuling,
  isAutomaticRulingOverridden
} from '../../helpers/proofHelpers';
import { resultLabel } from '../CustomResultDropdown';
import {
  AvChipTone,
  AvDivider,
  AvDocumentChip,
  DocumentChip,
  DocumentChipBadge,
  DocumentChipList,
  SwitcherBar,
  SwitcherHeading
} from './styles';

interface IProps {
  proofs: IProof[];
  avRuledProofs?: IProof[];
  selectedProofId?: string;
  onProofChange: (proofId: string) => void;
  localFormData?: Record<string, FormValues>;
}

const avTone = (result?: string): AvChipTone =>
  result === EntryResultEnum.Clean
    ? 'clean'
    : result === EntryResultEnum.Edited
    ? 'edited'
    : result === EntryResultEnum.Undetermined
    ? 'insufficient'
    : 'other';

const DocumentSwitcher: FC<IProps> = ({
  proofs,
  avRuledProofs = [],
  selectedProofId,
  onProofChange,
  localFormData = {}
}) => {
  if (proofs.length === 0 && avRuledProofs.length === 0) return null;

  return (
    <SwitcherBar data-testid="inspector-document-switcher">
      <SwitcherHeading>Documents</SwitcherHeading>
      <DocumentChipList>
        {proofs.map((proof, index) => {
          const ruling = getProofRuling(proof, localFormData);
          const label = resultLabel(ruling?.result);
          return (
            <DocumentChip
              key={proof.id}
              type="button"
              data-testid={`inspector-document-chip-${proof.id}`}
              isCurrent={proof.id === selectedProofId}
              aria-current={proof.id === selectedProofId}
              aria-label={`Document ${index + 1} of ${proofs.length}${label ? `, ${label}` : ''}`}
              onClick={() => onProofChange(proof.id)}
            >
              {index + 1}
              {ruling && (
                <DocumentChipBadge
                  src={ruling.icon}
                  alt=""
                  data-testid={`inspector-document-chip-ruling-${proof.id}`}
                />
              )}
            </DocumentChip>
          );
        })}
        {proofs.length > 0 && avRuledProofs.length > 0 && (
          <AvDivider aria-hidden="true" data-testid="inspector-document-av-divider" />
        )}
        {avRuledProofs.map((proof, index) => {
          const number = proofs.length + index + 1;
          const automaticLabel = resultLabel(proof.result);
          const automaticText = `Ruled${
            automaticLabel ? ` ${automaticLabel}` : ''
          } by automated review`;
          const isOverridden = isAutomaticRulingOverridden(proof, localFormData[proof.id]);
          const override = isOverridden ? getProofRuling(proof, localFormData) : null;
          const overrideText = override
            ? `changed to ${resultLabel(override.result)}`
            : 'changed, not finished';
          const tone = isOverridden ? avTone(override?.result) : avTone(proof.result);
          const badge = isOverridden
            ? override?.icon
            : tone !== 'other'
            ? getIconPath(getAttrEntryResult(proof.result as EntryResultType).icon)
            : undefined;
          return (
            <AvDocumentChip
              key={proof.id}
              type="button"
              data-testid={`inspector-document-chip-${proof.id}`}
              tone={tone}
              isCurrent={proof.id === selectedProofId}
              aria-current={proof.id === selectedProofId}
              aria-label={`Document ${number}, AV ruled${
                automaticLabel ? ` ${automaticLabel}` : ''
              }${isOverridden ? `, ${overrideText}` : ''}`}
              title={isOverridden ? `${automaticText}, ${overrideText}` : automaticText}
              onClick={() => onProofChange(proof.id)}
            >
              {number}
              {badge && (
                <DocumentChipBadge
                  src={badge}
                  alt=""
                  data-testid={`inspector-document-chip-ruling-${proof.id}`}
                />
              )}
            </AvDocumentChip>
          );
        })}
      </DocumentChipList>
    </SwitcherBar>
  );
};

export default DocumentSwitcher;
